import React, { useEffect, useMemo, useState } from "react";
import Api from "../../../Api/Api";
import { useUserController } from "context/user";
import userModel from "../../../assets/data/user";

const styles = {
  panel: {
    width: "100%",
    marginTop: 20,
    padding: 16,
    border: "1px solid rgba(0, 22, 66, 0.2)",
    borderRadius: 8,
    background: "#f9fbfc",
  },
  title: {
    margin: "0 0 6px 0",
    fontSize: 20,
    color: "#001642",
  },
  subtitle: {
    margin: "0 0 16px 0",
    fontSize: 13,
    color: "#4f5b6f",
  },
  section: {
    border: "1px solid rgba(0, 22, 66, 0.16)",
    borderRadius: 6,
    background: "#fff",
    padding: 12,
    marginBottom: 12,
  },
  sectionTitle: {
    margin: "0 0 10px 0",
    fontSize: 15,
    color: "#001642",
  },
  row: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
  },
  input: {
    padding: 8,
    border: "1px solid rgba(0, 22, 66, 0.32)",
    borderRadius: 4,
    minHeight: 34,
    minWidth: 220,
  },
  select: {
    padding: 8,
    border: "1px solid rgba(0, 22, 66, 0.32)",
    borderRadius: 4,
    minHeight: 34,
    minWidth: 220,
    background: "#fff",
  },
  button: {
    border: "none",
    borderRadius: 4,
    padding: "8px 10px",
    color: "#fff",
    background: "#1266d3",
    cursor: "pointer",
  },
  buttonSecondary: {
    border: "none",
    borderRadius: 4,
    padding: "8px 10px",
    color: "#111",
    background: "#d7dce2",
    cursor: "pointer",
  },
  buttonDanger: {
    border: "none",
    borderRadius: 4,
    padding: "8px 10px",
    color: "#fff",
    background: "#9b1c1c",
    cursor: "pointer",
  },
  groupsWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 10,
    maxHeight: 240,
    overflowY: "auto",
  },
  groupCard: {
    border: "1px solid rgba(0, 22, 66, 0.2)",
    borderRadius: 6,
    padding: 10,
    background: "#fff",
    cursor: "pointer",
  },
  groupCardSelected: {
    border: "1px solid #1266d3",
    background: "#eef5ff",
  },
  tableWrap: {
    width: "100%",
    overflowX: "auto",
    border: "1px solid rgba(0, 22, 66, 0.12)",
    borderRadius: 6,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 920,
  },
  th: {
    textAlign: "left",
    background: "#f2f5f8",
    borderBottom: "1px solid rgba(0, 22, 66, 0.15)",
    padding: 8,
    fontSize: 12,
  },
  td: {
    borderBottom: "1px solid rgba(0, 22, 66, 0.1)",
    padding: 8,
    fontSize: 12,
    verticalAlign: "top",
  },
  message: {
    marginTop: 8,
    fontSize: 12,
    color: "#25384f",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: "#25384f",
    userSelect: "none",
  },
  error: {
    marginTop: 8,
    fontSize: 12,
    color: "#9b1c1c",
  },
  warningList: {
    margin: "8px 0 0 18px",
    fontSize: 12,
    color: "#774f00",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 8,
    marginTop: 8,
  },
  summaryCard: {
    border: "1px solid rgba(0, 22, 66, 0.16)",
    borderRadius: 6,
    padding: 8,
    background: "#fbfdff",
    fontSize: 12,
  },
  confirmInput: {
    padding: 8,
    border: "1px solid rgba(0, 22, 66, 0.32)",
    borderRadius: 4,
    minHeight: 34,
    minWidth: 280,
  },
};

const generateIdempotencyKey = () =>
  `dup-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

function DuplicateEmployeesPanel() {
  const [userController] = useUserController();
  const actorEmail = userController?.user?.email || userController?.user?.Email || "";

  const [searchTerm, setSearchTerm] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [jobClassificationFilter, setJobClassificationFilter] = useState("");
  const [jobNameFilter, setJobNameFilter] = useState("");
  const [terminatedIncluded, setTerminatedIncluded] = useState(true);

  const [companyOptions, setCompanyOptions] = useState([]);
  const [jobClassificationOptions, setJobClassificationOptions] = useState([]);
  const [jobNameOptions, setJobNameOptions] = useState([]);

  const [groups, setGroups] = useState([]);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanMessage, setScanMessage] = useState("");
  const [scanError, setScanError] = useState("");

  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [targetUserId, setTargetUserId] = useState(null);
  const [sourceUserIds, setSourceUserIds] = useState([]);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState("");

  const [executeLoading, setExecuteLoading] = useState(false);
  const [executeResult, setExecuteResult] = useState(null);
  const [executeError, setExecuteError] = useState("");
  const [confirmationText, setConfirmationText] = useState("");

  const selectedGroup = useMemo(
    () => groups.find((group) => group.groupId === selectedGroupId) || null,
    [groups, selectedGroupId]
  );

  useEffect(() => {
    let active = true;
    const instance = process.env.REACT_APP_APP_INSTANCE;

    const setDefaultOptions = () => {
      if (!active) return;
      setCompanyOptions([...(userModel?.companyId?.[instance] || [])]);
      setJobClassificationOptions([...(userModel?.classification || [])]);
      setJobNameOptions([]);
    };

    const loadFilterOptions = async () => {
      try {
        const response = await Api.getAllUsers("", { useCache: true });
        const users = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.users)
            ? response.data.users
            : [];

        const companySet = new Set(userModel?.companyId?.[instance] || []);
        const jobClassificationSet = new Set(userModel?.classification || []);
        const jobNameSet = new Set();

        users.forEach((user) => {
          if (user?.CompanyID) companySet.add(user.CompanyID);
          if (user?.JobClassification) jobClassificationSet.add(user.JobClassification);
          if (user?.JobName) jobNameSet.add(user.JobName);
        });

        if (!active) return;

        setCompanyOptions([...companySet].sort((a, b) => a.localeCompare(b)));
        setJobClassificationOptions(
          [...jobClassificationSet].sort((a, b) => a.localeCompare(b))
        );
        setJobNameOptions([...jobNameSet].sort((a, b) => a.localeCompare(b)));
      } catch (error) {
        setDefaultOptions();
      }
    };

    loadFilterOptions();
    return () => {
      active = false;
    };
  }, []);

  const clearScanFilters = () => {
    setSearchTerm("");
    setCompanyFilter("");
    setJobClassificationFilter("");
    setJobNameFilter("");
    setTerminatedIncluded(true);
  };

  const resetSelection = () => {
    setSelectedGroupId("");
    setTargetUserId(null);
    setSourceUserIds([]);
    setPreviewData(null);
    setPreviewError("");
    setExecuteResult(null);
    setExecuteError("");
    setConfirmationText("");
  };

  const applyGroupSelection = (group) => {
    setSelectedGroupId(group.groupId);
    setPreviewData(null);
    setPreviewError("");
    setExecuteResult(null);
    setExecuteError("");
    setConfirmationText("");

    const sortedMembers = [...group.members].sort((a, b) => {
      const aTerminated = Boolean(a.Terminated);
      const bTerminated = Boolean(b.Terminated);
      if (aTerminated !== bTerminated) return aTerminated ? 1 : -1;
      return `${a.LastName || ""}${a.FirstName || ""}`.localeCompare(
        `${b.LastName || ""}${b.FirstName || ""}`
      );
    });

    const defaultTarget = sortedMembers[0]?.UserID || null;
    const defaultSources = sortedMembers
      .map((member) => member.UserID)
      .filter((userId) => userId !== defaultTarget);

    setTargetUserId(defaultTarget);
    setSourceUserIds(defaultSources);
  };

  const runScan = async () => {
    setScanLoading(true);
    setScanError("");
    setScanMessage("");
    setPreviewData(null);
    setPreviewError("");
    setExecuteResult(null);
    setExecuteError("");

    try {
      const company = companyFilter.trim();
      const search = searchTerm.trim();
      const jobClassification = jobClassificationFilter.trim();
      const jobName = jobNameFilter.trim();

      const response = await Api.scanDuplicateEmployees({
        terminatedIncluded,
        companyFilter: company ? [company] : [],
        searchTerm: search,
        jobClassificationFilter: jobClassification,
        jobNameFilter: jobName,
        limitGroups: 100,
      });
      const nextGroups = Array.isArray(response?.data?.groups) ? response.data.groups : [];
      setGroups(nextGroups);

      if (!nextGroups.length) {
        resetSelection();
        setScanMessage("No duplicate groups were detected with the current filter.");
        return;
      }

      setScanMessage(`Found ${nextGroups.length} possible duplicate group(s).`);
      applyGroupSelection(nextGroups[0]);
    } catch (error) {
      const message =
        error?.response?.data?.message || "Duplicate scan failed. Please try again.";
      setScanError(message);
      setGroups([]);
      resetSelection();
    } finally {
      setScanLoading(false);
    }
  };

  const handleTargetChange = (userId) => {
    setTargetUserId(userId);
    setSourceUserIds((previous) => previous.filter((id) => id !== userId));
    setPreviewData(null);
    setPreviewError("");
    setExecuteResult(null);
    setExecuteError("");
    setConfirmationText("");
  };

  const toggleSource = (userId) => {
    if (userId === targetUserId) return;
    setSourceUserIds((previous) =>
      previous.includes(userId)
        ? previous.filter((id) => id !== userId)
        : [...previous, userId]
    );
    setPreviewData(null);
    setPreviewError("");
    setExecuteResult(null);
    setExecuteError("");
    setConfirmationText("");
  };

  const canPreview = Boolean(selectedGroup && targetUserId && sourceUserIds.length > 0);

  const runPreview = async () => {
    if (!canPreview) return;
    if (!actorEmail) {
      setPreviewError("Unable to identify the admin actor email from the current session.");
      return;
    }

    setPreviewLoading(true);
    setPreviewError("");
    setPreviewData(null);
    setExecuteResult(null);
    setExecuteError("");

    try {
      const response = await Api.previewDuplicateConsolidation({
        actorEmail,
        targetUserId,
        sourceUserIds,
      });
      setPreviewData(response?.data || null);
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to build consolidation preview.";
      setPreviewError(message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const runConsolidation = async () => {
    if (!previewData) {
      setExecuteError("Run preview before consolidation.");
      return;
    }
    if (confirmationText !== "CONSOLIDATE") {
      setExecuteError('Type "CONSOLIDATE" exactly to continue.');
      return;
    }
    if (!actorEmail) {
      setExecuteError("Unable to identify the admin actor email from the current session.");
      return;
    }

    setExecuteLoading(true);
    setExecuteError("");
    setExecuteResult(null);

    try {
      const response = await Api.consolidateDuplicateEmployees({
        actorEmail,
        targetUserId,
        sourceUserIds,
        confirmationText,
        idempotencyKey: generateIdempotencyKey(),
      });

      setExecuteResult(response?.data || null);
      setConfirmationText("");
    } catch (error) {
      const message =
        error?.response?.data?.message || "Consolidation failed. Please review and retry.";
      setExecuteError(message);
    } finally {
      setExecuteLoading(false);
    }
  };

  return (
    <div style={styles.panel}>
      <h3 style={styles.title}>Duplicate Employee Consolidation</h3>
      <p style={styles.subtitle}>
        Find duplicate accounts, choose one account to keep, preview what will move, then consolidate.
        Consolidation copies certificates and reassigns key training history to the kept account.
      </p>

      <div style={styles.section}>
        <h4 style={styles.sectionTitle}>1. Find Duplicate Groups</h4>
        <div style={styles.row}>
          <input
            style={styles.input}
            placeholder="Search users (name/email)"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <select
            style={styles.select}
            value={companyFilter}
            onChange={(event) => setCompanyFilter(event.target.value)}
          >
            <option value="">All Companies</option>
            {companyOptions.map((company) => (
              <option key={company} value={company}>
                {company}
              </option>
            ))}
          </select>
          <select
            style={styles.select}
            value={jobClassificationFilter}
            onChange={(event) => setJobClassificationFilter(event.target.value)}
          >
            <option value="">All Job Classifications</option>
            {jobClassificationOptions.map((jobClassification) => (
              <option key={jobClassification} value={jobClassification}>
                {jobClassification}
              </option>
            ))}
          </select>
          <select
            style={styles.select}
            value={jobNameFilter}
            onChange={(event) => setJobNameFilter(event.target.value)}
          >
            <option value="">All Job Names</option>
            {jobNameOptions.map((jobName) => (
              <option key={jobName} value={jobName}>
                {jobName}
              </option>
            ))}
          </select>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={terminatedIncluded}
              onChange={(event) => setTerminatedIncluded(event.target.checked)}
            />
            Include terminated
          </label>
        </div>
        <div style={styles.row}>
          <button
            type="button"
            style={styles.button}
            onClick={runScan}
            disabled={scanLoading}
          >
            {scanLoading ? "Scanning..." : "Run Scan"}
          </button>
          <button
            type="button"
            style={styles.buttonSecondary}
            onClick={resetSelection}
          >
            Reset Selection
          </button>
          <button
            type="button"
            style={styles.buttonSecondary}
            onClick={clearScanFilters}
          >
            Clear Scan Filters
          </button>
        </div>
        {scanMessage && <p style={styles.message}>{scanMessage}</p>}
        {scanError && <p style={styles.error}>{scanError}</p>}

        {!!groups.length && (
          <div style={styles.groupsWrap}>
            {groups.map((group) => (
              <div
                key={group.groupId}
                style={{
                  ...styles.groupCard,
                  ...(selectedGroupId === group.groupId ? styles.groupCardSelected : {}),
                }}
                onClick={() => applyGroupSelection(group)}
              >
                <strong>
                  {group.members.length} account(s) | confidence {group.confidence}
                </strong>
                <div style={styles.message}>Signals: {group.reasons.join(", ")}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedGroup && (
        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>2. Choose Account To Keep</h4>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Keep</th>
                  <th style={styles.th}>Merge From</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Company</th>
                  <th style={styles.th}>Job Class</th>
                  <th style={styles.th}>Job Name</th>
                  <th style={styles.th}>Terminated</th>
                </tr>
              </thead>
              <tbody>
                {selectedGroup.members.map((member) => {
                  const selectedAsTarget = targetUserId === member.UserID;
                  return (
                    <tr key={member.UserID}>
                      <td style={styles.td}>
                        <input
                          type="radio"
                          name="duplicate-target-user"
                          checked={selectedAsTarget}
                          onChange={() => handleTargetChange(member.UserID)}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={sourceUserIds.includes(member.UserID)}
                          onChange={() => toggleSource(member.UserID)}
                          disabled={selectedAsTarget}
                        />
                      </td>
                      <td style={styles.td}>{`${member.FirstName || ""} ${member.LastName || ""}`.trim()}</td>
                      <td style={styles.td}>{member.Email}</td>
                      <td style={styles.td}>{member.CompanyID || "-"}</td>
                      <td style={styles.td}>{member.JobClassification || "-"}</td>
                      <td style={styles.td}>{member.JobName || "-"}</td>
                      <td style={styles.td}>{member.Terminated ? "Yes" : "No"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p style={styles.message}>
            Keep user: <strong>{targetUserId || "none"}</strong> | Merge source count:{" "}
            <strong>{sourceUserIds.length}</strong>
          </p>
        </div>
      )}

      {selectedGroup && (
        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>3. Preview Consolidation</h4>
          <div style={styles.row}>
            <button
              type="button"
              style={styles.button}
              onClick={runPreview}
              disabled={!canPreview || previewLoading}
            >
              {previewLoading ? "Building Preview..." : "Run Preview"}
            </button>
          </div>
          {!canPreview && (
            <p style={styles.message}>Select one kept user and at least one source user.</p>
          )}
          {previewError && <p style={styles.error}>{previewError}</p>}
          {previewData && (
            <>
              <div style={styles.summaryGrid}>
                <div style={styles.summaryCard}>
                  <strong>Certificates</strong>
                  <div>To Copy: {previewData?.certificates?.toCopyCount || 0}</div>
                  <div>
                    Name Conflicts: {previewData?.certificates?.potentialNameConflicts || 0}
                  </div>
                  <div>Already Present: {previewData?.certificates?.alreadyPresentCount || 0}</div>
                </div>
                <div style={styles.summaryCard}>
                  <strong>Quiz Tracking</strong>
                  <div>Rows To Reassign: {previewData?.quizTracking?.rowsToReassign || 0}</div>
                </div>
                <div style={styles.summaryCard}>
                  <strong>Video Logs</strong>
                  <div>Source Docs: {previewData?.videoLogs?.sourceDocs || 0}</div>
                  <div>New Keys Estimate: {previewData?.videoLogs?.mergedKeysEstimate || 0}</div>
                </div>
              </div>
              {!!previewData?.warnings?.length && (
                <ul style={styles.warningList}>
                  {previewData.warnings.map((warning, index) => (
                    <li key={`${warning}-${index}`}>{warning}</li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      {selectedGroup && (
        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>4. Confirm And Consolidate</h4>
          <div style={styles.row}>
            <input
              style={styles.confirmInput}
              placeholder='Type "CONSOLIDATE" to confirm'
              value={confirmationText}
              onChange={(event) => setConfirmationText(event.target.value)}
            />
            <button
              type="button"
              style={styles.buttonDanger}
              onClick={runConsolidation}
              disabled={executeLoading || !previewData}
            >
              {executeLoading ? "Consolidating..." : "Consolidate Accounts"}
            </button>
          </div>
          <p style={styles.message}>
            Source accounts are terminated after consolidation. Certificates are copied to the kept account.
          </p>
          {executeError && <p style={styles.error}>{executeError}</p>}
          {executeResult && (
            <div style={styles.summaryCard}>
              <div>
                <strong>Status:</strong> {executeResult.status}
              </div>
              <div>
                <strong>Job ID:</strong> {executeResult.jobId}
              </div>
              <div>
                <strong>Certificates Copied:</strong> {executeResult?.results?.certsCopied || 0}
              </div>
              <div>
                <strong>Quiz Rows Reassigned:</strong>{" "}
                {executeResult?.results?.quizRowsReassigned || 0}
              </div>
              <div>
                <strong>Video Docs Merged:</strong> {executeResult?.results?.videoDocsMerged || 0}
              </div>
              <div>
                <strong>Users Terminated:</strong> {executeResult?.results?.usersTerminated || 0}
              </div>
              {!!executeResult?.warnings?.length && (
                <ul style={styles.warningList}>
                  {executeResult.warnings.map((warning, index) => (
                    <li key={`${warning}-${index}`}>{warning}</li>
                  ))}
                </ul>
              )}
              {!!executeResult?.errors?.length && (
                <ul style={styles.error}>
                  {executeResult.errors.map((error, index) => (
                    <li key={`${error}-${index}`}>{error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DuplicateEmployeesPanel;
